import React, { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  Legend,
} from 'recharts';
import { usePilgrimGuideStore } from '../../stores/pilgrimGuideStore';
import { useLanguage } from '../../contexts/LanguageContext';

export const CountryDistributionChart = () => {
  const { links } = usePilgrimGuideStore();
  const { translations } = useLanguage();
  const linksList = Object.values(links);

  // Calculate country distribution and disputes
  const countryStats = useMemo(() => {
    const stats = linksList.reduce((acc, link) => {
      const country = link.residenceCountry;

      // Initialize country if not exists
      if (!acc[country]) {
        acc[country] = {
          country,
          total: 0,
          disputes: 0,
        };
      }

      // Increment total
      acc[country].total++;

      // Increment disputes if disputed
      if (link.status === 'disputed') {
        acc[country].disputes++;
      }

      return acc;
    }, {} as Record<string, { country: string; total: number; disputes: number }>);

    // Convert to array and sort by total
    return Object.values(stats)
      .map((stat) => ({
        ...stat,
        disputes: Math.floor(stat.disputes / 2), // Divide disputes by 2 and round up
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 10); // Only show top 10 countries
  }, [linksList]);

  // Generate colors array
  const colors = [
    '#00897B',
    '#26A69A',
    '#4DB6AC',
    '#80CBC4',
    '#B2DFDB',
    '#C5A572',
    '#D4B483',
    '#E3C394',
    '#F2D2A5',
    '#FFE0B2',
  ];

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-100">
          <p className="font-medium">{label}</p>
          <p className="text-primary-600">
            {translations?.countryDistribution?.total || 'Total'}:{' '}
            {payload[0].value}
          </p>
          {payload[1] && (
            <p className="text-red-600">
              {translations?.countryDistribution?.disputes || 'Disputes'}:{' '}
              {payload[1].value}
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white p-6 rounded-xl shadow-sm">
      <h3 className="text-lg font-semibold mb-6">
        {translations?.countryDistribution?.title ||
          'Pilgrims Distribution by Country'}
      </h3>
      <div className="h-[400px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={countryStats}
            layout="vertical"
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" />
            <YAxis
              dataKey="country"
              type="category"
              width={200}
              tick={{ fontSize: 12 }}
              mirror={true}
              orientation="right"
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Bar
              dataKey="total"
              name={
                translations?.countryDistribution?.total || 'Total Pilgrims'
              }
              fill="#00897B"
              barSize={20}
            >
              {countryStats.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={colors[index % colors.length]}
                />
              ))}
            </Bar>
            <Bar
              dataKey="disputes"
              name={translations?.countryDistribution?.disputes || 'Disputes'}
              fill="#EF4444"
              barSize={20}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
